import React, { useRef } from 'react';
import {
  CheckBoxParam,
  ComboBoxMultiselectParam,
  Form, HiddenParam, Row,
  SelectParam,
  SonForm, SubmitButton,
  TextAreaParam,
  TextParam,
  useForm,
} from '../../../../_lib/Form2';
import { DateRangeParam } from '../../../../_lib/Form2/types/DateRangeParam';
import AnnouncementCreateOrUpdateTemplate from './AnnouncementCreateOrUpdateTemplate';
import useRedirect from '../../../../hooks/useRedirect';
import { translatePartOfStringWithLink } from '../../../../lib/StringUtils/StringHTMLUtils';
import { Namespace } from '../../../../translation/namespace';
import { useAppContext } from '../../../../contexts/AppContext/AppContext';
import { useSonTranslation } from '../../../../translation/useSonTranslation';

export type AnnouncementCreateOrUpdateForm = SonForm<
  | TextParam<'title'>
  | SelectParam<'categoryId'>
  | TextParam<'linkedPageTitle'>
  | CheckBoxParam<'sendToAll'>
  | CheckBoxParam<'mandatory'>
  | ComboBoxMultiselectParam<'recipient', any> & { hasReadProtectedItems: boolean }
  | TextAreaParam<'text'>
  | DateRangeParam<'daterange'>
  | HiddenParam<'announcementId'>
>;

export type AnnouncementCreateOrUpdateProps = {
  form: AnnouncementCreateOrUpdateForm;
  type: 'create' | 'update';
};

export function AnnouncementCreateOrUpdate({
  form,
  type,
}: Readonly<AnnouncementCreateOrUpdateProps>) {
  const { t } = useSonTranslation('Announcement');
  const { portalConfiguration } = useAppContext();

  const getIdByType = () => {
    switch (type) {
      case 'create':
        return 'announcement-create-form';
      case 'update':
      default:
        return 'announcement-update-form';
    }
  };
  const [startRedirect] = useRedirect();
  const { formProps, fetchState } = useForm({ form, onSuccess: (value) => {
    if (value.data.url) {
      startRedirect(value.data.url);
    }
  } });
  const formRef = useRef<HTMLFormElement>(null);

  return (
    <Form {...formProps} id={getIdByType()} ref={formRef}>
      <AnnouncementCreateOrUpdateTemplate form={form} />
      <Row modifier="no-margin">
        <SubmitButton
          buttonLabel={t(`form.saveButton.${type}`)}
          messageFail={translatePartOfStringWithLink(
            `${Namespace.Announcement}:form.status.${type}.fail`,
            portalConfiguration && `mailto:${portalConfiguration.son_portal_support_email}`,
          )}
          messageSuccess={t(`form.status.${type}.success`)}
          messageForward={t(`${Namespace.Announcement}:form.status.${type}.successForward`)}
          messageSending={t(`form.status.${type}.sending`)}
          customErrorTypeMessages={
            // The logic defined inside attribute "active" guarantees that each required field must be set before the
            // form submit button is enabled. But to harden the unlikely case that somehow no recipient is chosen and
            // the form has been submitted nonetheless, the backend itself aborts the request by sending "invalidValue"
            // as displayable error type, which is then handled via the following expression:
            {
              invalidValue: t(`${Namespace.Form}:errorType.invalidValue`),
            }
          }
          fetchState={fetchState}
        />
      </Row>
    </Form>
  );
}
